import { useState } from 'react'
import Lottie from 'lottie-react'
import { Maximize2, RotateCcw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import submitCompleteAnimation from '@/assets/lottie/subjective-submit-complete.json'
import { PauseSubjectiveResultPopup } from '@/components/learner/pause/PauseSubjectiveResultPopup'
import { Button, type ButtonSize, type ButtonVariant } from '@/components/learner/ui'
import { ReviewCompletionDialog } from '@/components/special-lecture/ReviewCompletionDialog'

type Swatch = {
  name: string
  hex: string
  className: string
  dark?: boolean
}

type TypeSpecimen = {
  name: string
  metric: string
  className: string
  sample?: 'label'
}

const keySwatches: Swatch[] = [
  { name: 'Key/50', hex: '#E8F2FF', className: 'bg-lc-key-50' },
  { name: 'Key/100', hex: '#C7DFFF', className: 'bg-lc-key-100' },
  { name: 'Key/200', hex: '#9FC8FF', className: 'bg-lc-key-200' },
  { name: 'Key/300', hex: '#79B3FF', className: 'bg-lc-key-300' },
  { name: 'Key/400', hex: '#5CA6FF', className: 'bg-lc-key-400' },
  { name: 'Key/500', hex: '#4599FF', className: 'bg-lc-key-500' },
  { name: 'Key/600', hex: '#3A82E0', className: 'bg-lc-key-600', dark: true },
  { name: 'Key/700', hex: '#2F6BBF', className: 'bg-lc-key-700', dark: true },
  { name: 'Key/800', hex: '#24549E', className: 'bg-lc-key-800', dark: true },
  { name: 'Key/900', hex: '#193D7D', className: 'bg-lc-key-900', dark: true },
  { name: 'Key/950', hex: '#132548', className: 'bg-lc-key-950', dark: true },
]

const graySwatches: Swatch[] = [
  { name: 'Gray/0', hex: '#FFFFFF', className: 'bg-lc-v3-gray-0' },
  { name: 'Gray/50', hex: '#FAFAFA', className: 'bg-lc-v3-gray-50' },
  { name: 'Gray/100', hex: '#F5F5F5', className: 'bg-lc-v3-gray-100' },
  { name: 'Gray/200', hex: '#EEEEEE', className: 'bg-lc-v3-gray-200' },
  { name: 'Gray/300', hex: '#E0E0E0', className: 'bg-lc-v3-gray-300' },
  { name: 'Gray/400', hex: '#BDBDBD', className: 'bg-lc-v3-gray-400' },
  { name: 'Gray/500', hex: '#9E9E9E', className: 'bg-lc-v3-gray-500' },
  { name: 'Gray/600', hex: '#757575', className: 'bg-lc-v3-gray-600', dark: true },
  { name: 'Gray/700', hex: '#616161', className: 'bg-lc-v3-gray-700', dark: true },
  { name: 'Gray/800', hex: '#424242', className: 'bg-lc-v3-gray-800', dark: true },
  { name: 'Gray/900', hex: '#212121', className: 'bg-lc-v3-gray-900', dark: true },
  { name: 'Gray/950', hex: '#121212', className: 'bg-lc-v3-gray-950', dark: true },
]

const statusSwatches: Swatch[] = [
  { name: 'Success/50', hex: '#EAF9F2', className: 'bg-lc-success-50' },
  { name: 'Success/500', hex: '#2AC47E', className: 'bg-lc-success-500' },
  { name: 'Success/900', hex: '#145E3C', className: 'bg-lc-success-900', dark: true },
  { name: 'Warning/50', hex: '#FFFAE9', className: 'bg-lc-warning-50' },
  { name: 'Warning/500', hex: '#FFCE1F', className: 'bg-lc-warning-500' },
  { name: 'Warning/900', hex: '#E07A00', className: 'bg-lc-warning-900', dark: true },
  { name: 'Error/50', hex: '#FFF0EF', className: 'bg-lc-error-50' },
  { name: 'Error/500', hex: '#FF675E', className: 'bg-lc-error-500' },
  { name: 'Error/900', hex: '#7A312D', className: 'bg-lc-error-900', dark: true },
]

const typeSpecimens: TypeSpecimen[] = [
  { name: 'Display 1', metric: '56 / 1.3 / -1.5', className: 'text-lc-v3-display-1 font-bold' },
  { name: 'Display 2', metric: '48 / 1.3 / -1', className: 'text-lc-v3-display-2 font-bold' },
  { name: 'Heading 1', metric: '36 / 1.4 / -0.5', className: 'text-lc-v3-h1 font-bold' },
  { name: 'Heading 2', metric: '28 / 1.4 / -0.3', className: 'text-lc-v3-h2 font-bold' },
  { name: 'Heading 3', metric: '24 / 1.45 / -0.2', className: 'text-lc-v3-h3 font-semibold' },
  { name: 'Heading 4', metric: '20 / 1.45 / -0.1', className: 'text-lc-v3-h4 font-semibold' },
  { name: 'Heading 5', metric: '18 / 1.5 / 0', className: 'text-lc-v3-h5 font-semibold' },
  { name: 'Body 1', metric: '16 / 1.6 / 0', className: 'text-lc-v3-body-1 font-normal' },
  { name: 'Body 2', metric: '14 / 1.6 / 0.1', className: 'text-lc-v3-body-2 font-normal' },
  { name: 'Caption 1', metric: '12 / 1.5 / 0.2', className: 'text-lc-v3-caption-1 font-normal' },
  { name: 'Caption 2', metric: '11 / 1.5 / 0.3', className: 'text-lc-v3-caption-2 font-normal' },
  { name: 'Overline', metric: '12 / 1.5 / 1.5', className: 'text-lc-v3-overline font-semibold uppercase' },
  { name: 'Label L', metric: '16 / 1 / 0.2', className: 'text-lc-v3-label-lg font-semibold', sample: 'label' },
  { name: 'Label M', metric: '14 / 1 / 0.2', className: 'text-lc-v3-label-md font-semibold', sample: 'label' },
  { name: 'Label S', metric: '12 / 1 / 0.3', className: 'text-lc-v3-label-sm font-medium', sample: 'label' },
]

const buttonVariants: ButtonVariant[] = ['primary', 'navy', 'neutral', 'outline', 'ghost']
const buttonSizes: ButtonSize[] = ['S', 'M', 'L', 'XL']

function SectionHeader({
  index,
  title,
  description,
}: {
  index: string
  title: string
  description: string
}) {
  return (
    <div className="grid gap-2 border-b border-lc-v3-gray-300 pb-5 md:grid-cols-[72px_1fr] md:gap-5">
      <span className="text-lc-v3-overline font-semibold text-lc-key-600">{index}</span>
      <div className="space-y-1">
        <h2 className="text-lc-v3-h2 font-bold text-lc-text-primary">{title}</h2>
        <p className="max-w-3xl text-lc-v3-body-2 text-lc-text-secondary">{description}</p>
      </div>
    </div>
  )
}

function SwatchGrid({ title, items }: { title: string; items: Swatch[] }) {
  return (
    <div className="space-y-3">
      <h3 className="text-lc-v3-h5 font-semibold text-lc-text-primary">{title}</h3>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {items.map((swatch) => (
          <div
            key={swatch.name}
            className={`flex min-h-24 flex-col justify-end rounded-xl border border-black/5 p-3 ${swatch.className}`}
          >
            <span
              className={`text-lc-v3-caption-1 font-semibold ${
                swatch.dark ? 'text-lc-text-inverse' : 'text-lc-text-primary'
              }`}
            >
              {swatch.name}
            </span>
            <span
              className={`text-lc-v3-caption-2 ${
                swatch.dark ? 'text-lc-text-inverse/75' : 'text-lc-text-secondary'
              }`}
            >
              {swatch.hex}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default function DesignSystemDemo() {
  const { t } = useTranslation('learner')
  const [animationKey, setAnimationKey] = useState(0)
  const [isResultPopupOpen, setIsResultPopupOpen] = useState(false)
  const [isReviewCompletionOpen, setIsReviewCompletionOpen] = useState(false)

  return (
    <div className="min-h-full bg-lc-bg-secondary pb-20">
      <header className="relative isolate overflow-hidden bg-lc-key-950 text-lc-text-on-dark">
        <div className="absolute -right-24 -top-36 -z-10 size-96 rounded-full border-[72px] border-lc-key-800/55" />
        <div className="absolute -bottom-28 right-1/3 -z-10 size-56 rounded-full bg-lc-key-900/70" />
        <div className="mx-auto max-w-6xl px-5 py-14 md:px-10 md:py-20">
          <div className="max-w-3xl space-y-6">
            <span className="inline-flex rounded-full border border-lc-key-400/45 bg-lc-key-900/70 px-3 py-1.5 text-lc-v3-overline font-semibold text-lc-key-100">
              {t('designSystem.hero.eyebrow')}
            </span>
            <div className="space-y-3">
              <h1 className="text-lc-v3-display-2 font-bold text-lc-text-on-dark">
                {t('designSystem.hero.title')}
              </h1>
              <p className="max-w-2xl text-lc-v3-body-1 text-lc-key-100">
                {t('designSystem.hero.description')}
              </p>
            </div>
            <dl className="grid max-w-2xl grid-cols-2 gap-x-8 gap-y-5 border-t border-lc-key-700 pt-6 sm:grid-cols-4">
              {[
                ['11', t('designSystem.hero.keySteps')],
                ['15', t('designSystem.hero.typeStyles')],
                ['5 × 4', t('designSystem.hero.buttonMatrix')],
                ['V3', t('designSystem.hero.foundation')],
              ].map(([value, label]) => (
                <div key={label}>
                  <dt className="text-lc-v3-caption-1 text-lc-key-200">{label}</dt>
                  <dd className="mt-1 text-lc-v3-h4 font-bold text-lc-text-on-dark">{value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </header>

      <nav
        aria-label={t('designSystem.nav.label')}
        className="sticky top-0 z-10 border-b border-lc-v3-gray-300 bg-lc-bg-primary/95 shadow-lc-v3-header backdrop-blur"
      >
        <div className="mx-auto flex max-w-6xl gap-1 overflow-x-auto px-5 py-2 md:px-10">
          {(['colors', 'type', 'buttons', 'surfaces', 'motion', 'dialogs'] as const).map((item) => (
            <a
              key={item}
              href={`#${item}`}
              className="shrink-0 rounded-lg px-3 py-2 text-lc-v3-label-md font-semibold text-lc-text-secondary hover:bg-lc-key-50 hover:text-lc-key-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500"
            >
              {t(`designSystem.nav.${item}`)}
            </a>
          ))}
        </div>
      </nav>

      <main className="mx-auto max-w-6xl space-y-16 px-5 py-12 md:px-10 md:py-16">
        <section id="colors" className="scroll-mt-20 space-y-8">
          <SectionHeader
            index="01"
            title={t('designSystem.colors.title')}
            description={t('designSystem.colors.description')}
          />
          <SwatchGrid title={t('designSystem.colors.key')} items={keySwatches} />
          <SwatchGrid title={t('designSystem.colors.gray')} items={graySwatches} />
          <SwatchGrid title={t('designSystem.colors.status')} items={statusSwatches} />

          <div className="space-y-3">
            <h3 className="text-lc-v3-h5 font-semibold text-lc-text-primary">
              {t('designSystem.colors.semantic')}
            </h3>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-2xl border border-lc-v3-gray-300 bg-lc-bg-primary p-5">
                <span className="text-lc-v3-caption-1 text-lc-text-tertiary">Background/Primary</span>
                <p className="mt-6 text-lc-v3-body-1 font-semibold text-lc-text-primary">Text/Primary</p>
                <p className="text-lc-v3-body-2 text-lc-text-secondary">Text/Secondary</p>
              </div>
              <div className="rounded-2xl border border-lc-key-100 bg-lc-bg-key-light p-5">
                <span className="text-lc-v3-caption-1 text-lc-text-link">Background/Key Light</span>
                <p className="mt-6 text-lc-v3-body-1 font-semibold text-lc-text-primary">
                  {t('designSystem.colors.keySurface')}
                </p>
              </div>
              <div className="rounded-2xl bg-lc-success-50 p-5">
                <span className="text-lc-v3-caption-1 text-lc-text-success">Text/Success</span>
                <p className="mt-6 text-lc-v3-body-1 font-semibold text-lc-text-primary">
                  {t('designSystem.colors.successSurface')}
                </p>
              </div>
              <div className="rounded-2xl bg-lc-error-50 p-5">
                <span className="text-lc-v3-caption-1 text-lc-text-error">Text/Error</span>
                <p className="mt-6 text-lc-v3-body-1 font-semibold text-lc-text-primary">
                  {t('designSystem.colors.errorSurface')}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="type" className="scroll-mt-20 space-y-8">
          <SectionHeader
            index="02"
            title={t('designSystem.typography.title')}
            description={t('designSystem.typography.description')}
          />
          <div className="divide-y divide-lc-v3-gray-200 overflow-hidden rounded-2xl border border-lc-v3-gray-300 bg-lc-bg-primary shadow-lc-v3-card">
            {typeSpecimens.map((specimen) => (
              <div key={specimen.name} className="grid gap-4 p-5 md:grid-cols-[150px_1fr] md:p-6">
                <div>
                  <div className="text-lc-v3-label-md font-semibold text-lc-text-primary">{specimen.name}</div>
                  <div className="mt-1 text-lc-v3-caption-2 text-lc-text-tertiary">{specimen.metric}</div>
                </div>
                <p className={`min-w-0 break-keep text-lc-text-primary ${specimen.className}`}>
                  {t(
                    specimen.sample === 'label'
                      ? 'designSystem.typography.labelSample'
                      : 'designSystem.typography.sample',
                  )}
                </p>
              </div>
            ))}
          </div>
          <p className="rounded-xl bg-lc-warning-50 px-4 py-3 text-lc-v3-body-2 text-lc-text-warning">
            {t('designSystem.typography.weightNote')}
          </p>
        </section>

        <section id="buttons" className="scroll-mt-20 space-y-8">
          <SectionHeader
            index="03"
            title={t('designSystem.buttons.title')}
            description={t('designSystem.buttons.description')}
          />

          <div className="space-y-8 rounded-2xl border border-lc-v3-gray-300 bg-lc-bg-primary p-5 shadow-lc-v3-card md:p-7">
            <div className="space-y-4">
              <h3 className="text-lc-v3-h5 font-semibold text-lc-text-primary">
                {t('designSystem.buttons.variants')}
              </h3>
              <div className="flex flex-wrap items-center gap-3">
                {buttonVariants.map((variant) => (
                  <Button key={variant} variant={variant} size="L">
                    {variant}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-4 border-t border-lc-v3-gray-200 pt-7">
              <h3 className="text-lc-v3-h5 font-semibold text-lc-text-primary">
                {t('designSystem.buttons.sizes')}
              </h3>
              <div className="flex flex-wrap items-center gap-3">
                {buttonSizes.map((size) => (
                  <Button key={size} variant="primary" size={size}>
                    {size}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-4 border-t border-lc-v3-gray-200 pt-7">
              <h3 className="text-lc-v3-h5 font-semibold text-lc-text-primary">
                {t('designSystem.buttons.states')}
              </h3>
              <div className="flex flex-wrap items-center gap-3">
                <Button variant="primary" size="L">Default</Button>
                <Button variant="primary" size="L" disabled>Disabled</Button>
                <Button variant="outline" size="L">Outline</Button>
                <Button variant="outline" size="L" disabled>Disabled</Button>
              </div>
              <p className="text-lc-v3-caption-1 text-lc-text-tertiary">
                {t('designSystem.buttons.focusNote')}
              </p>
            </div>
          </div>
        </section>

        <section id="surfaces" className="scroll-mt-20 space-y-8">
          <SectionHeader
            index="04"
            title={t('designSystem.surfaces.title')}
            description={t('designSystem.surfaces.description')}
          />
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-2xl border border-lc-v3-gray-300 bg-lc-bg-primary p-6 shadow-lc-v3-header">
              <span className="text-lc-v3-overline font-semibold text-lc-key-600">HEADER</span>
              <h3 className="mt-10 text-lc-v3-h4 font-semibold text-lc-text-primary">shadow-lc-v3-header</h3>
              <p className="mt-1 text-lc-v3-body-2 text-lc-text-secondary">0 4px 8px · 5%</p>
            </div>
            <div className="rounded-2xl border border-lc-v3-gray-300 bg-lc-bg-primary p-6 shadow-lc-v3-card">
              <span className="text-lc-v3-overline font-semibold text-lc-key-600">CARD</span>
              <h3 className="mt-10 text-lc-v3-h4 font-semibold text-lc-text-primary">shadow-lc-v3-card</h3>
              <p className="mt-1 text-lc-v3-body-2 text-lc-text-secondary">0 4px 4px · 5%</p>
            </div>
            <div className="rounded-2xl border border-lc-key-200 bg-lc-bg-key-light p-6 shadow-lc-v3-active">
              <span className="text-lc-v3-overline font-semibold text-lc-key-700">ACTIVE</span>
              <h3 className="mt-10 text-lc-v3-h4 font-semibold text-lc-text-primary">shadow-lc-v3-active</h3>
              <p className="mt-1 text-lc-v3-body-2 text-lc-text-secondary">0 4px 4px · 20%</p>
            </div>
          </div>
          <p className="text-lc-v3-caption-1 text-lc-text-tertiary">
            {t('designSystem.surfaces.scopeNote')}
          </p>
        </section>

        <section id="motion" className="scroll-mt-20 space-y-8">
          <SectionHeader
            index="05"
            title={t('designSystem.subjectiveResult.sectionTitle')}
            description={t('designSystem.subjectiveResult.sectionDescription')}
          />

          <div className="overflow-hidden rounded-2xl border border-lc-v3-gray-300 bg-lc-bg-primary shadow-lc-v3-card">
            <div className="grid lg:grid-cols-[minmax(360px,1fr)_minmax(320px,0.85fr)]">
              <div className="relative flex min-h-[400px] items-center justify-center overflow-hidden bg-lc-key-950 p-5">
                <div className="absolute -left-24 -top-24 size-64 rounded-full border-[48px] border-lc-key-800/50" />
                <Lottie
                  key={animationKey}
                  data-testid="design-system-subjective-result-lottie"
                  animationData={submitCompleteAnimation}
                  autoplay
                  loop={false}
                  aria-hidden="true"
                  className="relative h-[360px] w-[360px] max-w-full"
                />
              </div>

              <div className="flex flex-col justify-between gap-8 bg-lc-bg-primary p-6 md:p-8">
                <div className="space-y-5">
                  <div className="space-y-2">
                    <span className="inline-flex rounded-full bg-lc-key-50 px-3 py-1 text-lc-v3-overline font-semibold text-lc-key-700">
                      {t('designSystem.subjectiveResult.badge')}
                    </span>
                    <h3 className="text-lc-v3-h3 font-bold text-lc-text-primary">
                      {t('pause.subjectiveSubmitted')}
                    </h3>
                    <p className="text-lc-v3-body-2 text-lc-text-secondary">
                      {t('designSystem.subjectiveResult.description')}
                    </p>
                  </div>

                  <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-lc-v3-caption-1">
                    <dt className="text-lc-text-tertiary">{t('designSystem.subjectiveResult.playbackLabel')}</dt>
                    <dd className="font-semibold text-lc-text-primary">
                      {t('designSystem.subjectiveResult.playbackValue')}
                    </dd>
                    <dt className="text-lc-text-tertiary">{t('designSystem.subjectiveResult.assetLabel')}</dt>
                    <dd className="break-all font-semibold text-lc-text-primary">
                      subjective-submit-complete.json
                    </dd>
                    <dt className="text-lc-text-tertiary">{t('designSystem.subjectiveResult.fontLabel')}</dt>
                    <dd className="font-semibold text-lc-text-primary">
                      {t('designSystem.subjectiveResult.fontValue')}
                    </dd>
                  </dl>
                </div>

                <div className="flex flex-wrap gap-3">
                  <Button
                    variant="primary"
                    size="M"
                    className="gap-2"
                    onClick={() => setAnimationKey((key) => key + 1)}
                  >
                    <RotateCcw className="size-4" aria-hidden="true" />
                    {t('designSystem.subjectiveResult.replay')}
                  </Button>
                  <Button
                    variant="outline"
                    size="M"
                    className="gap-2"
                    onClick={() => setIsResultPopupOpen(true)}
                  >
                    <Maximize2 className="size-4" aria-hidden="true" />
                    {t('designSystem.subjectiveResult.openPopup')}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <PauseSubjectiveResultPopup
            isOpen={isResultPopupOpen}
            onClose={() => setIsResultPopupOpen(false)}
          />
        </section>

        <section id="dialogs" className="scroll-mt-20 space-y-8">
          <SectionHeader
            index="06"
            title={t('designSystem.reviewCompletion.sectionTitle')}
            description={t('designSystem.reviewCompletion.sectionDescription')}
          />

          <div className="grid gap-6 overflow-hidden rounded-2xl border border-lc-v3-gray-300 bg-lc-bg-primary p-6 shadow-lc-v3-card md:grid-cols-[1fr_auto] md:items-center md:p-8">
            <div className="flex max-w-2xl flex-col gap-3">
              <span className="w-fit rounded-full bg-lc-success-50 px-3 py-1 text-lc-v3-overline font-semibold text-lc-text-success">
                {t('designSystem.reviewCompletion.badge')}
              </span>
              <h3 className="text-lc-v3-h3 font-bold text-lc-text-primary">
                {t('specialLecture.review.completionTitle')}
              </h3>
              <p className="text-lc-v3-body-2 text-lc-text-secondary">
                {t('designSystem.reviewCompletion.description')}
              </p>
            </div>

            <Button
              variant="primary"
              size="L"
              onClick={() => setIsReviewCompletionOpen(true)}
            >
              {t('designSystem.reviewCompletion.openPopup')}
            </Button>
          </div>

          <ReviewCompletionDialog
            open={isReviewCompletionOpen}
            onConfirm={() => setIsReviewCompletionOpen(false)}
          />
        </section>
      </main>
    </div>
  )
}
